import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	Menu,
	MenuItem,
	Pagination,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { saveAs } from "file-saver";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { SubHeader } from "../../utils/componentes-utiles/SubHeader";
import {
	descargarDocumentoPersona,
	getListadoDocumentosPersona,
} from "../../services/apis";
import { formatDateTimeEs } from "../../utils/formatters";

const getPersonaDocumentFallbackName = (documento) =>
	documento?.archivo_nombre || documento?.nombre || `documento-${documento?.id ?? "persona"}`;

const getDocumentName = (documento) =>
	documento?.nombre || documento?.archivo_nombre || "Documento sin nombre";

const isPdfDocument = (documento) => {
	const extension = String(documento?.extension || "").trim().toLowerCase();
	const mimeType = String(documento?.mime_type || "").trim().toLowerCase();
	const fileName = String(documento?.archivo_nombre || documento?.nombre || "").trim().toLowerCase();

	return extension === "pdf" || mimeType === "application/pdf" || fileName.endsWith(".pdf");
};

const getPersonaName = (documento) =>
	[documento?.persona?.apellidos, documento?.persona?.nombre]
		.filter(Boolean)
		.join(" ")
		.trim() ||
	documento?.persona?.nombre_completo ||
	`Persona ${documento?.id_usuario ?? "-"}`;

const getPersonaNif = (documento) => documento?.persona?.nif || "";

const getCampaignName = (documento) => documento?.campana_nombre || "Sin campaña";

const getFolderName = (documento) => documento?.carpeta_nombre || "Sin carpeta";

const normalizeText = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

const formatFileSize = (bytes) => {
	const size = Number(bytes || 0);

	if (size <= 0) return "-";
	if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;

	return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const EXPIRY_WARNING_DAYS = 15;

const hasExpiredOrPendingStatus = (estado) =>
	[
		"pendiente",
		"revision",
		"en revision",
		"en_revision",
		"caducado",
		"rechazado",
		"error",
		"inactivo",
	].includes(normalizeText(estado));

const getEstadoColor = (estado) => {
	const normalized = normalizeText(estado);

	if (["vigente", "activo", "ok", "validado"].includes(normalized)) {
		return "success";
	}

	if (hasExpiredOrPendingStatus(normalized)) {
		return "error";
	}

	return "default";
};

const getPrivacidadColor = (privacidad) =>
	normalizeText(privacidad) === "privado" ? "secondary" : "default";

const getExpiryColor = (value) => {
	const normalized = String(value || "").trim().slice(0, 10);

	if (!normalized) {
		return "default";
	}

	if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
		return "default";
	}

	const [year, month, day] = normalized.split("-").map(Number);
	const expiryUtc = Date.UTC(year, month - 1, day);
	const today = new Date();
	const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
	const diffDays = Math.floor((expiryUtc - todayUtc) / (1000 * 60 * 60 * 24));

	if (diffDays < 0) {
		return "error";
	}

	if (diffDays <= EXPIRY_WARNING_DAYS) {
		return "warning";
	}

	return "success";
};

export const Documentos = () => {
	const [documentos, setDocumentos] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [busqueda, setBusqueda] = useState("");
	const [filtroCampana, setFiltroCampana] = useState("");
	const [filtroCarpeta, setFiltroCarpeta] = useState("");
	const [filtroTipo, setFiltroTipo] = useState("");
	const [filtroPrivacidad, setFiltroPrivacidad] = useState("");
	const [filtroEstado, setFiltroEstado] = useState("");
	const [filtroFormato, setFiltroFormato] = useState("");
	const [page, setPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(25);
	const [menuAnchor, setMenuAnchor] = useState(null);
	const [menuDocumento, setMenuDocumento] = useState(null);
	const [downloadingId, setDownloadingId] = useState(null);
	const [previewingId, setPreviewingId] = useState(null);
	const [previewDocument, setPreviewDocument] = useState(null);
	const [previewError, setPreviewError] = useState("");

	const location = useLocation();
	const isActive = (path) => location.pathname === path;

	const cargarDocumentos = async () => {
		setLoading(true);
		setError("");

		try {
			const response = await getListadoDocumentosPersona();

			if (response?.success === true) {
				setDocumentos(Array.isArray(response?.data?.documentos) ? response.data.documentos : []);
				return;
			}

			setDocumentos([]);
			setError(response?.message || "No se pudo cargar la documentacion.");
		} catch (loadError) {
			console.error("Error cargando documentacion:", loadError);
			setDocumentos([]);
			setError("No se pudo cargar la documentacion.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		void cargarDocumentos();
	}, []);

	useEffect(() => {
		setPage(1);
	}, [busqueda, filtroCampana, filtroCarpeta, filtroTipo, filtroPrivacidad, filtroEstado, filtroFormato]);

	const campanasDisponibles = Array.from(
		new Map(
			documentos
				.map((documento) => [
					Number(documento?.id_campana || 0),
					String(getCampaignName(documento)).trim(),
				])
				.filter(([id, nombre]) => id > 0 && nombre)
		).entries()
	)
		.map(([id, nombre]) => ({ id, nombre }))
		.sort((left, right) => left.nombre.localeCompare(right.nombre, "es"));

	const carpetasDisponibles = Array.from(
		new Map(
			documentos
				.map((documento) => [
					Number(documento?.id_carpeta_documentacion || 0),
					`${getCampaignName(documento)} · ${String(documento?.carpeta_nombre || "").trim()}`,
				])
				.filter(([id, nombre]) => id > 0 && nombre)
		).entries()
	)
		.map(([id, nombre]) => ({ id, nombre }))
		.sort((left, right) => left.nombre.localeCompare(right.nombre, "es"));

	const tiposDocumento = Array.from(
		new Set(documentos.map((documento) => String(documento?.tipo || "").trim()).filter(Boolean))
	).sort((left, right) => left.localeCompare(right, "es"));

	const privacidadesDisponibles = Array.from(
		new Set(
			documentos.map((documento) => String(documento?.privacidad || "").trim()).filter(Boolean)
		)
	).sort((left, right) => left.localeCompare(right, "es"));

	const estadosDisponibles = Array.from(
		new Set(documentos.map((documento) => String(documento?.estado || "").trim()).filter(Boolean))
	).sort((left, right) => left.localeCompare(right, "es"));

	const formatosDisponibles = Array.from(
		new Set(
			documentos
				.map((documento) => String(documento?.extension || "").trim().toUpperCase())
				.filter(Boolean)
		)
	).sort((left, right) => left.localeCompare(right, "es"));

	const documentosFiltrados = documentos.filter((documento) => {
		const normalizedSearch = normalizeText(busqueda);
		const matchesSearch =
			normalizedSearch === "" ||
			[
				getDocumentName(documento),
				documento?.archivo_nombre,
				getCampaignName(documento),
				getFolderName(documento),
				getPersonaName(documento),
				getPersonaNif(documento),
				documento?.tipo,
				documento?.privacidad,
				documento?.estado,
				documento?.extension,
				documento?.fecha_caducidad,
			].some((value) => normalizeText(value).includes(normalizedSearch));

		const matchesCampana =
			filtroCampana === "" || Number(documento?.id_campana || 0) === Number(filtroCampana);
		const matchesCarpeta =
			filtroCarpeta === "" ||
			Number(documento?.id_carpeta_documentacion || 0) === Number(filtroCarpeta);
		const matchesTipo =
			filtroTipo === "" || String(documento?.tipo || "").trim() === filtroTipo;
		const matchesPrivacidad =
			filtroPrivacidad === "" ||
			String(documento?.privacidad || "").trim() === filtroPrivacidad;
		const matchesEstado =
			filtroEstado === "" || String(documento?.estado || "").trim() === filtroEstado;
		const matchesFormato =
			filtroFormato === "" ||
			String(documento?.extension || "").trim().toUpperCase() === filtroFormato;

		return (
			matchesSearch &&
			matchesCampana &&
			matchesCarpeta &&
			matchesTipo &&
			matchesPrivacidad &&
			matchesEstado &&
			matchesFormato
		);
	});

	const totalPages = Math.max(1, Math.ceil(documentosFiltrados.length / itemsPerPage));
	const startIndex = (page - 1) * itemsPerPage;
	const documentosPaginados = documentosFiltrados.slice(startIndex, startIndex + itemsPerPage);

	const handleOpenMenu = (event, documento) => {
		setMenuAnchor(event.currentTarget);
		setMenuDocumento(documento);
	};

	const handleCloseMenu = () => {
		setMenuAnchor(null);
		setMenuDocumento(null);
	};

	const handleDownloadDocument = async (documento) => {
		if (!documento?.id) {
			return;
		}

		setDownloadingId(documento.id);

		try {
			const { blob, fileName } = await descargarDocumentoPersona(documento.id);
			saveAs(blob, fileName || getPersonaDocumentFallbackName(documento));
		} catch (downloadError) {
			console.error("Error descargando documento:", downloadError);
			setError("No se pudo descargar el documento seleccionado.");
		} finally {
			setDownloadingId(null);
			handleCloseMenu();
		}
	};

	const handlePreviewPdf = async (documento) => {
		if (!documento?.id || !isPdfDocument(documento)) {
			return;
		}

		setPreviewingId(documento.id);
		setPreviewError("");

		try {
			const { blob, fileName } = await descargarDocumentoPersona(documento.id);
			const pdfBlob = blob?.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
			const url = URL.createObjectURL(pdfBlob);

			setPreviewDocument((current) => {
				if (current?.url) {
					URL.revokeObjectURL(current.url);
				}

				return {
					documento,
					fileName: fileName || getPersonaDocumentFallbackName(documento),
					url,
				};
			});
		} catch (previewLoadError) {
			console.error("Error previsualizando documento:", previewLoadError);
			setPreviewError("No se pudo previsualizar el PDF seleccionado.");
		} finally {
			setPreviewingId(null);
			handleCloseMenu();
		}
	};

	const closePdfPreview = () => {
		setPreviewDocument((current) => {
			if (current?.url) {
				URL.revokeObjectURL(current.url);
			}

			return null;
		});
		setPreviewError("");
	};

	useEffect(() => () => {
		if (previewDocument?.url) {
			URL.revokeObjectURL(previewDocument.url);
		}
	}, [previewDocument?.url]);

	const limpiarFiltros = () => {
		setBusqueda("");
		setFiltroCampana("");
		setFiltroCarpeta("");
		setFiltroTipo("");
		setFiltroPrivacidad("");
		setFiltroEstado("");
		setFiltroFormato("");
	};

	return (
		<Grid container direction="column" size={{ xs: 12 }}>
			<Grid item size={{ xs: 12 }}>
				<SubHeader titulo="Documentacion" />
			</Grid>

			<Grid container spacing={2} sx={{ px: 2, pb: 2 }} alignItems="center">
				<Button
					variant="text"
					component={RouterLink}
					to="/personas"
					sx={{
						fontWeight: isActive("/personas") ? 600 : 400,
						borderBottom: isActive("/personas") ? "2px solid" : "2px solid transparent",
						borderRadius: 0,
					}}
				>
					Personas
				</Button>

				<Button
					variant="text"
					component={RouterLink}
					to="/carpetas"
					sx={{
						fontWeight: isActive("/carpetas") ? 600 : 400,
						borderBottom: isActive("/carpetas") ? "2px solid" : "2px solid transparent",
						borderRadius: 0,
					}}
				>
					Carpetas
				</Button>

				<Button
					variant="text"
					component={RouterLink}
					to="/documentacion"
					sx={{
						fontWeight: isActive("/documentacion") ? 600 : 400,
						borderBottom: isActive("/documentacion")
							? "2px solid"
							: "2px solid transparent",
						borderRadius: 0,
					}}
				>
					Documentos
				</Button>
			</Grid>

			<Grid
				container
				justifyContent="space-between"
				alignItems="center"
				sx={{ px: 2, pb: 2 }}
				gap={2}
			>
				<Grid container spacing={1} size={{ xs: 12, md: "auto" }}>
					<TextField
						size="small"
						placeholder="Buscar por documento, campaña, carpeta, persona o NIF..."
						value={busqueda}
						onChange={(event) => setBusqueda(event.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
						sx={{ width: { xs: "100%", md: 320 } }}
					/>

					<FormControl size="small" sx={{ minWidth: 180 }}>
						<InputLabel>Campaña</InputLabel>
						<Select
							value={filtroCampana}
							label="Campaña"
							onChange={(event) => setFiltroCampana(event.target.value)}
						>
							<MenuItem value="">Todas</MenuItem>
							{campanasDisponibles.map((campana) => (
								<MenuItem key={`document-campaign-${campana.id}`} value={String(campana.id)}>
									{campana.nombre}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel>Carpeta</InputLabel>
						<Select
							value={filtroCarpeta}
							label="Carpeta"
							onChange={(event) => setFiltroCarpeta(event.target.value)}
						>
							<MenuItem value="">Todas</MenuItem>
							{carpetasDisponibles.map((carpeta) => (
								<MenuItem key={`document-folder-${carpeta.id}`} value={String(carpeta.id)}>
									{carpeta.nombre}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel>Tipo</InputLabel>
						<Select
							value={filtroTipo}
							label="Tipo"
							onChange={(event) => setFiltroTipo(event.target.value)}
						>
							<MenuItem value="">Todos</MenuItem>
							{tiposDocumento.map((tipo) => (
								<MenuItem key={tipo} value={tipo}>
									{tipo}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel>Privacidad</InputLabel>
						<Select
							value={filtroPrivacidad}
							label="Privacidad"
							onChange={(event) => setFiltroPrivacidad(event.target.value)}
						>
							<MenuItem value="">Todas</MenuItem>
							{privacidadesDisponibles.map((privacidad) => (
								<MenuItem key={privacidad} value={privacidad}>
									{privacidad}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel>Estado</InputLabel>
						<Select
							value={filtroEstado}
							label="Estado"
							onChange={(event) => setFiltroEstado(event.target.value)}
						>
							<MenuItem value="">Todos</MenuItem>
							{estadosDisponibles.map((estado) => (
								<MenuItem key={estado} value={estado}>
									{estado}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 120 }}>
						<InputLabel>Formato</InputLabel>
						<Select
							value={filtroFormato}
							label="Formato"
							onChange={(event) => setFiltroFormato(event.target.value)}
						>
							<MenuItem value="">Todos</MenuItem>
							{formatosDisponibles.map((formato) => (
								<MenuItem key={formato} value={formato}>
									{formato}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<Button
						size="small"
						variant="outlined"
						onClick={limpiarFiltros}
						disabled={
							!busqueda &&
							!filtroCampana &&
							!filtroCarpeta &&
							!filtroTipo &&
							!filtroPrivacidad &&
							!filtroEstado &&
							!filtroFormato
						}
					>
						Limpiar
					</Button>
				</Grid>

				<Grid container spacing={1} size={{ xs: 12, md: "auto" }}>
					<Button
						variant="outlined"
						startIcon={<RefreshIcon />}
						onClick={cargarDocumentos}
						disabled={loading}
					>
						Actualizar
					</Button>
				</Grid>
			</Grid>

			<Grid container sx={{ px: 2, pb: 2 }}>
				<Alert severity="info" sx={{ width: "100%" }}>
					Aqui se muestran los documentos reales subidos en personas, indicando la
					campaña y la carpeta a la que pertenecen, junto con su fecha de caducidad
					cuando aplica.
				</Alert>
			</Grid>

			{loading && (
				<Grid container justifyContent="center" sx={{ py: 5 }}>
					<CircularProgress />
				</Grid>
			)}

			{!loading && error && (
				<Grid container sx={{ px: 2, pb: 2 }}>
					<Alert
						severity="error"
						action={
							<Button color="inherit" size="small" onClick={cargarDocumentos}>
								Reintentar
							</Button>
						}
						sx={{ width: "100%" }}
					>
						{error}
					</Alert>
				</Grid>
			)}

			{!loading && !error && (
				<>
					<Grid sx={{ width: "100%", overflowX: "auto" }}>
						<Grid
							container
							direction="column"
							sx={{ minWidth: { xs: 1360, md: 1540 } }}
						>
							<Grid
								container
								alignItems="center"
								sx={{
									px: 2,
									py: 1,
									bgcolor: "grey.100",
									borderTop: "1px solid",
									borderBottom: "1px solid",
									borderColor: "divider",
								}}
							>
								<Grid item size={{ xs: 0.6 }} />
								<Grid item size={{ xs: 2.5 }}>Documento</Grid>
								<Grid item size={{ xs: 1.6 }}>Campaña</Grid>
								<Grid item size={{ xs: 1.8 }}>Carpeta</Grid>
								<Grid item size={{ xs: 2.0 }}>Persona</Grid>
								<Grid item size={{ xs: 1.1 }}>Tipo</Grid>
								<Grid item size={{ xs: 1.0 }}>Tamano</Grid>
								<Grid item size={{ xs: 1.0 }}>Privacidad</Grid>
								<Grid item size={{ xs: 1.1 }}>Estado</Grid>
								<Grid item size={{ xs: 1.0 }}>Caducidad</Grid>
								<Grid item size={{ xs: 0.9 }}>Formato</Grid>
								<Grid item size={{ xs: 1.0 }}>Fecha</Grid>
							</Grid>

							{documentosPaginados.length === 0 ? (
								<Grid container justifyContent="center" sx={{ py: 4 }}>
									<Typography color="text.secondary">
										No se encontraron documentos con esos filtros.
									</Typography>
								</Grid>
							) : (
								documentosPaginados.map((documento) => (
									<Grid
										key={documento.id}
										container
										alignItems="center"
										sx={{
											px: 2,
											py: 1,
											borderBottom: "1px solid",
											borderColor: "divider",
											"&:hover": { bgcolor: "action.hover" },
										}}
									>
										<Grid item size={{ xs: 0.6 }}>
											<IconButton
												size="small"
												onClick={(event) => handleOpenMenu(event, documento)}
											>
												<MoreVertIcon fontSize="small" />
											</IconButton>
										</Grid>

										<Grid
											item
											size={{ xs: 2.5 }}
											sx={{ display: "flex", alignItems: "center", gap: 1 }}
										>
											<InsertDriveFileIcon color="action" fontSize="small" />
											<Grid container direction="column" size={{ xs: 12 }}>
												<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
													{getDocumentName(documento)}
												</Typography>
												<Typography variant="caption" color="text.secondary" noWrap>
													{documento?.archivo_nombre || "-"}
												</Typography>
											</Grid>
										</Grid>

										<Grid item size={{ xs: 1.6 }}>
											<Chip
												size="small"
												variant="outlined"
												label={getCampaignName(documento)}
											/>
										</Grid>

										<Grid item size={{ xs: 1.8 }}>
											<Chip
												size="small"
												variant="outlined"
												label={getFolderName(documento)}
											/>
										</Grid>

										<Grid item size={{ xs: 2.0 }}>
											<Grid
												container
												direction="column"
												size={{ xs: 12 }}
												component={documento?.id_usuario ? RouterLink : "div"}
												to={documento?.id_usuario ? `/personas/${documento.id_usuario}` : undefined}
												state={
													documento?.id_usuario
														? {
																persona: {
																	id: documento.id_usuario,
																	nombre: documento?.persona?.nombre || "",
																	apellidos: documento?.persona?.apellidos || "",
																	nif: documento?.persona?.nif || "",
																	nombre_completo: documento?.persona?.nombre_completo || "",
																},
																from: "/documentacion",
														  }
														: undefined
												}
												sx={{
													cursor: documento?.id_usuario ? "pointer" : "default",
													textDecoration: "none",
													color: "inherit",
													"&:hover .persona-link": documento?.id_usuario
														? {
																color: "primary.main",
																textDecoration: "underline",
														  }
														: undefined,
												}}
											>
												<Typography
													className="persona-link"
													variant="body2"
													sx={{ fontWeight: 600, transition: "color 0.2s ease" }}
													noWrap
												>
													{getPersonaName(documento)}
												</Typography>
												{getPersonaNif(documento) && (
													<Typography
														className="persona-link"
														variant="caption"
														color="text.secondary"
														sx={{ transition: "color 0.2s ease" }}
														noWrap
													>
														{getPersonaNif(documento)}
													</Typography>
												)}
											</Grid>
										</Grid>

										<Grid item size={{ xs: 1.1 }}>
											<Typography variant="body2" noWrap>
												{documento?.tipo || "-"}
											</Typography>
										</Grid>

										<Grid item size={{ xs: 1.0 }}>
											<Typography variant="body2">{formatFileSize(documento?.tamano_bytes)}</Typography>
										</Grid>

										<Grid item size={{ xs: 1.0 }}>
											<Chip
												size="small"
												color={getPrivacidadColor(documento?.privacidad)}
												label={documento?.privacidad || "-"}
											/>
										</Grid>

										<Grid item size={{ xs: 1.1 }}>
											<Chip
												size="small"
												color={getEstadoColor(documento?.estado)}
												label={documento?.estado || "-"}
											/>
										</Grid>

										<Grid item size={{ xs: 1.0 }}>
											{documento?.fecha_caducidad ? (
												<Chip
													size="small"
													color={getExpiryColor(documento?.fecha_caducidad)}
													label={formatDateTimeEs(documento?.fecha_caducidad, {
														includeTime: false,
													})}
												/>
											) : (
												<Typography variant="body2" color="text.secondary">
													-
												</Typography>
											)}
										</Grid>

										<Grid item size={{ xs: 0.9 }}>
											<Chip
												size="small"
												variant="outlined"
												label={String(documento?.extension || "-").toUpperCase()}
											/>
										</Grid>

										<Grid item size={{ xs: 1.0 }}>
											<Typography variant="body2" noWrap>
												{formatDateTimeEs(documento?.fecha_creacion, {
													includeTime: false,
												})}
											</Typography>
										</Grid>
									</Grid>
								))
							)}
						</Grid>
					</Grid>

					{documentosFiltrados.length > 0 && (
						<Grid
							container
							justifyContent="space-between"
							alignItems="center"
							sx={{ px: 2, py: 2 }}
							gap={2}
						>
							<FormControl variant="standard" sx={{ minWidth: 140 }}>
								<InputLabel>Filas por pagina</InputLabel>
								<Select
									value={itemsPerPage}
									onChange={(event) => {
										setItemsPerPage(Number(event.target.value));
										setPage(1);
									}}
								>
									<MenuItem value={10}>10</MenuItem>
									<MenuItem value={25}>25</MenuItem>
									<MenuItem value={50}>50</MenuItem>
									<MenuItem value={100}>100</MenuItem>
								</Select>
							</FormControl>

							<Typography variant="body2" color="text.secondary">
								{documentosFiltrados.length} documento(s) encontrados
							</Typography>

							<Pagination
								count={totalPages}
								page={page}
								onChange={(_, value) => setPage(value)}
								color="primary"
							/>
						</Grid>
					)}
				</>
			)}

			<Menu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor) && Boolean(menuDocumento)}
				onClose={handleCloseMenu}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<MenuItem
					component={menuDocumento?.id_usuario ? RouterLink : "li"}
					to={menuDocumento?.id_usuario ? `/personas/${menuDocumento.id_usuario}` : undefined}
					state={
						menuDocumento?.id_usuario
							? {
									persona: {
										id: menuDocumento.id_usuario,
										nombre: menuDocumento?.persona?.nombre || "",
										apellidos: menuDocumento?.persona?.apellidos || "",
										nif: menuDocumento?.persona?.nif || "",
										nombre_completo: menuDocumento?.persona?.nombre_completo || "",
									},
									from: "/documentacion",
							  }
							: undefined
					}
					onClick={handleCloseMenu}
					disabled={!menuDocumento?.id_usuario}
				>
					<VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
					Ver persona
				</MenuItem>

				<MenuItem
					onClick={() => {
						if (menuDocumento) {
							void handlePreviewPdf(menuDocumento);
						}
					}}
					disabled={
						!menuDocumento?.id ||
						!isPdfDocument(menuDocumento) ||
						previewingId === menuDocumento?.id
					}
				>
					<VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
					{previewingId === menuDocumento?.id ? "Abriendo..." : "Previsualizar PDF"}
				</MenuItem>

				<MenuItem
					onClick={() => {
						if (menuDocumento) {
							void handleDownloadDocument(menuDocumento);
						}
					}}
					disabled={!menuDocumento?.id || downloadingId === menuDocumento?.id}
				>
					<DownloadOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
					{downloadingId === menuDocumento?.id ? "Descargando..." : "Descargar"}
				</MenuItem>
			</Menu>

			<Dialog open={Boolean(previewDocument)} onClose={closePdfPreview} maxWidth="lg" fullWidth>
				<DialogTitle sx={{ pb: 1 }}>
					{previewDocument?.fileName || "Documento PDF"}
				</DialogTitle>
				<DialogContent sx={{ p: 0, height: { xs: "72vh", md: "78vh" } }}>
					{previewDocument?.url && (
						<Box
							component="iframe"
							src={previewDocument.url}
							title={previewDocument.fileName || "Previsualizacion PDF"}
							sx={{
								width: "100%",
								height: "100%",
								border: 0,
								display: "block",
							}}
						/>
					)}
				</DialogContent>
				<DialogActions>
					{previewError && (
						<Typography variant="body2" color="error" sx={{ mr: "auto" }}>
							{previewError}
						</Typography>
					)}
					<Button onClick={closePdfPreview}>Cerrar</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};
